import { useContext, useEffect, useRef } from 'react';
import useMeasure, { type RectReadOnly } from 'react-use-measure';

import { IntroContext } from '../context.ts';

type UseHighlightComponentRectType<T extends HTMLElement> = (
  element: T | null,
) => void;

function useHighlightComponentRect<T extends HTMLElement = HTMLDivElement>(
  step: number,
): UseHighlightComponentRectType<T> {
  const { activeStep, setHighlightComponentRect } = useContext(IntroContext);
  const [ref, rect] = useMeasure({
    debounce: 250,
    scroll: true,
    offsetSize: true,
  });

  const prevRectRef = useRef<RectReadOnly | null>(null);

  useEffect(() => {
    if (activeStep !== step) return;

    const rectChanged =
      !prevRectRef.current ||
      JSON.stringify(prevRectRef.current) !== JSON.stringify(rect);

    if (rectChanged) {
      prevRectRef.current = rect;
      setHighlightComponentRect(rect);
    }

    return (): void => {
      if (activeStep === step) {
        setHighlightComponentRect(null);
      }
    };
  }, [activeStep, step, rect]);

  return ref as UseHighlightComponentRectType<T>;
}

export { useHighlightComponentRect, type UseHighlightComponentRectType };
